import { useTranslation } from 'react-i18next';
import Linkify from 'linkify-react';
import type { IntermediateRepresentation } from 'linkifyjs';
import { format_num } from '../../utils/format/format_num';
import FullscreenAssetViewer from '../primitives/FullscreenAssetViewer';
import { cn } from '../cn';
import type { RealEstateCatalogItem } from './realEstateCatalog';


const SHELL_ICON = '/ui/shell-icon.png';
const STAR_ICON = '/ui/telegram-stars-icon.png';
const FIRST_HOME_DISCOUNT_ICON = '/ui/first-home-discount-icon.webp';

// Використовуємо format_num для форматування валюти
const formatSoftCurrency = (value: number): string => format_num(value);

interface RealEstateCardProps {
  item: RealEstateCatalogItem;
  plotId: number | null;
  isProcessing: boolean;
  isOnboarding: boolean;
  onBuyWithPearls: (realEstateId: number) => void;
  onBuyWithStars: (realEstateId: number) => void;
}

export default function RealEstateCard({
  item,
  plotId,
  isProcessing,
  isOnboarding,
  onBuyWithPearls,
  onBuyWithStars,
}: RealEstateCardProps) {
  const { t } = useTranslation();
  const buyDisabled = isProcessing || plotId == null;

  return (
    <article
      className="relative flex flex-col gap-lg rounded-xl border-2 border-solid border-daily-quest-auxiliary-modals-border-2 px-2xl py-[13px]"
      style={{
        background: `linear-gradient(90deg, ${item.gradient.from} 0%, ${item.gradient.via ?? item.gradient.to} 56.25%, ${item.gradient.to} 98.08%)`,
      }}
    >
      {/* Badges container — always shown: stock badge is unconditional; discount badge is conditional inside */}
      <div className="flex w-full flex-wrap items-center justify-center gap-sm">
        {/* First-home discount badge */}
        {item.hasFirstHouseDiscount && (
          <div className="flex items-center justify-center gap-sm rounded-pill border border-danger-strong bg-random-box-surface-2 px-lg py-[3px]">
            <img
              src={FIRST_HOME_DISCOUNT_ICON}
              alt=""
              className="h-[14.208px] w-[16.5px] shrink-0"
            />
            <p className="m-0 whitespace-nowrap [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-normal text-white">
              {t("First home, sweet home")}
            </p>
          </div>
        )}

        {/* Quantity indicator — not in onboarding (name, price, description and Buy only) */}
        {!isOnboarding && <div className="flex items-center justify-center gap-xs rounded-pill bg-black-alpha-60 px-lg py-[3px]">
          <span className="whitespace-nowrap [font-family:Lato,sans-serif] text-[12px] leading-[16px] font-normal text-white">
            {item.maxQuantity == null ? (
              t("In stock: unlimited")
            ) : item.isAvailable ? (
              t("In stock: {{quantity}}", { quantity: item.maxQuantity - item.purchasedCount })
            ) : (
              t("Released: {{quantity}}", { quantity: item.maxQuantity })
            )}
          </span>
          {item.maxQuantity != null && item.isAvailable && (item.maxQuantity - item.purchasedCount) === 1 && (
            <span className="text-[12px]">🔥</span>
          )}
        </div>}
      </div>

      {/* Sold out overlay */}
      {!item.isAvailable && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black-alpha-70">
          <div className="rounded-md border-2 border-solid border-danger-strong bg-random-box-surface-2 px-xl py-md">
            <p className="m-0 whitespace-nowrap [font-family:Lato,sans-serif] text-[14px] font-bold text-white">
              {t("Sold out")}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-nowrap items-center justify-center gap-lg">
        <FullscreenAssetViewer
          src={item.image}
          alt={item.name}
          thumbnailStyle={{
            width: 'min(200px, 30vw)',
            aspectRatio: '128 / 135',
            minWidth: 80,
            maxWidth: 'min(200px, 30vw)',
            borderRadius: 18,
            objectFit: 'contain',
            objectPosition: '50% 50%',
          }}
        />
        <div className="flex min-w-0 w-auto flex-[0_1_auto] flex-col items-center gap-md">
          {/* Price display above the button */}
          {item.canBuyWithPearls && (
            <div className="flex items-center justify-center gap-xs">
              {(item.hasFirstHouseDiscount || item.hasGeneralDiscount) && item.priceSoft != null && (
                <span className="[font-family:Lato,sans-serif] text-[12px] font-normal text-white-alpha-50 line-through [text-decoration-skip-ink:none]">
                  {formatSoftCurrency(item.priceSoft)}
                </span>
              )}
              <span
                className={cn(
                  '[font-family:"Rubik_One",sans-serif] text-[16px] font-normal',
                  item.hasFirstHouseDiscount ? 'text-error-soft' : 'text-white',
                )}
              >
                {formatSoftCurrency(item.effectivePearlsPrice ?? item.priceSoft ?? 0)}
              </span>
              <img
                src={SHELL_ICON}
                alt={t("Shell")}
                draggable={false}
                className="h-[18px] w-[22px] shrink-0"
              />
            </div>
          )}
          {/* Green "Buy Now" button — pearls. Green light: the gold one blended
              into the golden price and cards and did not read as "you can buy this". */}
          {item.canBuyWithPearls && (
            <button
              className={cn(
                'box-border inline-flex w-fit items-center justify-center gap-[5px] rounded-pill px-[min(16px,4.5vw)] py-[6px] [font-family:"Rubik_One",sans-serif] text-[14px] leading-[29px] font-normal tracking-[-0.747px] whitespace-nowrap text-white [text-shadow:0_1px_2px_var(--color-black-alpha-35)]',
                buyDisabled
                  ? 'cursor-not-allowed border-none bg-go-alpha-45'
                  : 'go-button cursor-pointer',
                // Onboarding: a light sweeps across it, so Buy is unmissable
                isOnboarding && !buyDisabled && 'go-button--shine',
              )}
              onClick={() => onBuyWithPearls(item.id)}
              disabled={buyDisabled}
            >
              {isProcessing ? (
                <>
                  <span className="inline-block h-[14px] w-[14px] animate-[spin_0.6s_linear_infinite] rounded-full border-2 border-white-alpha-20 border-t-white" />
                  {t("Processing...")}
                </>
              ) : t("Buy Now")}
            </button>
          )}
          {/* Stars buy button — only for stars-only houses (no pearls price) */}
          {item.canBuyWithStars && !item.canBuyWithPearls && (
            <div className="flex flex-col items-center gap-sm">
              <div className="flex items-center justify-center gap-xs">
                <span className='[font-family:"Rubik_One",sans-serif] text-[16px] font-normal text-white'>
                  {item.effectiveStarsPrice}
                </span>
                <img src={STAR_ICON} alt="Stars" draggable={false} className="h-[18px] w-[18px] shrink-0" />
              </div>
              <button
                onClick={() => onBuyWithStars(item.id)}
                disabled={buyDisabled}
                className={cn(
                  'box-border inline-flex w-fit items-center justify-center gap-[5px] rounded-pill border-none px-[min(16px,4.5vw)] py-[6px] [font-family:"Rubik_One",sans-serif] text-[14px] leading-[29px] font-normal tracking-[-0.747px] whitespace-nowrap text-white shadow-[0_4px_12px_var(--color-onboarding-glow-alpha-40)]',
                  buyDisabled
                    ? 'cursor-not-allowed bg-duel-magic-alpha-50'
                    : 'cursor-pointer bg-[linear-gradient(135deg,var(--color-real-estate-card-accent)_0%,var(--color-real-estate-card-accent-2)_100%)]',
                )}
              >
                {isProcessing ? (
                  <>
                    <span className="inline-block h-[14px] w-[14px] animate-[spin_0.6s_linear_infinite] rounded-full border-2 border-white-alpha-20 border-t-white" />
                    {t("Processing...")}
                  </>
                ) : t("Buy Now")}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="w-full">
        <p className="m-0 self-stretch [font-family:Lato,sans-serif] text-[16px] leading-[29px] font-bold tracking-[-0.747px] text-white not-italic">
          {item.name}
        </p>
        <Linkify
          as="p"
          options={{
            target: '_blank',
            rel: 'noopener noreferrer',
            formatHref: { mention: (href: string) => 'https://t.me' + href },
            render: ({ attributes, content }: IntermediateRepresentation) => {
              return (
                <a {...attributes} className="linkified-link">
                  {content}
                </a>
              );
            },
          }}
          className="mt-[6px] self-stretch [font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] text-[14px] leading-[20px] font-normal tracking-[0px] text-white not-italic"
        >
          {item.description}
        </Linkify>
      </div>
    </article>
  );
}
